const PLACEHOLDER_EN_PROCESO = /\/EnProceso_zoom2x\.png(?:[?#]|$)/i;

export const urlDeImagen = (imagen) => {
    if (typeof imagen === 'string') return imagen;
    return imagen?.urlImagen ?? imagen?.UrlImagen ?? '';
};

// La miniatura de proceso es la misma URL para todas las fotos que todavía no se generaron.
export const esImagenSinProcesar = (imagen) => {
    const url = String(urlDeImagen(imagen) ?? '');
    return !url.trim() || PLACEHOLDER_EN_PROCESO.test(url);
};

const imagenesDe = (publicacion) => {
    const imagenes = publicacion?.imagenes ?? publicacion?.Imagenes;
    return Array.isArray(imagenes) ? imagenes : null;
};

// Alcanza con una foto sin procesar: mirar solo la primera escondía el botón de reproceso.
export const esPublicacionSinImagen = (publicacion) => {
    const imagenes = imagenesDe(publicacion);
    if (imagenes) {
        if (!imagenes.length) return true;
        return imagenes.some(esImagenSinProcesar);
    }
    const imagen = publicacion?.imagen ?? publicacion?.Imagen;
    if (!imagen) return true;
    return esImagenSinProcesar(imagen);
};

export const miniaturaPublicacion = (publicacion) => {
    const imagenes = imagenesDe(publicacion) ?? [];
    const pendienteConUrl = imagenes.find((imagen) => {
        const url = String(urlDeImagen(imagen) ?? '').trim();
        return url && esImagenSinProcesar(imagen);
    });
    const portada = pendienteConUrl
        || publicacion?.imagen
        || publicacion?.Imagen
        || imagenes.find((imagen) => String(urlDeImagen(imagen) ?? '').trim());
    const url = String(urlDeImagen(portada) ?? '').trim();
    return url || null;
};

// La URL no sirve como identidad: las fotos en proceso comparten el placeholder.
export const prepararImagenes = (imagenes) => {
    const usadas = new Set();
    return (imagenes || []).map((img, index) => {
        const urlImagen = urlDeImagen(img);
        const idImagen = img?.idImagen ?? img?.IdImagen ?? null;
        let clientKey = img?.clientKey;
        if (!clientKey) {
            const tieneId = idImagen != null && String(idImagen) !== '';
            clientKey = tieneId ? `id-${idImagen}` : `local-${index}`;
        }
        const raiz = clientKey;
        let repeticion = 1;
        while (usadas.has(clientKey)) {
            clientKey = `${raiz}-${repeticion}`;
            repeticion += 1;
        }
        usadas.add(clientKey);
        return {
            ...(img && typeof img === 'object' ? img : {}),
            urlImagen,
            idImagen,
            clientKey,
            posicion: index
        };
    });
};

export const ordenarImagenes = (imagenes) =>
    [...(imagenes || [])].sort((a, b) =>
        Number(a?.posicion ?? a?.Posicion ?? 0) - Number(b?.posicion ?? b?.Posicion ?? 0)
    );

export const obtenerElementosPublicaciones = (response) =>
    response?.content ?? response?.Content ?? response?.elementos ?? response?.Elementos ?? [];

// Recorrer todas las páginas antes de filtrar conserva los totales y permite
// reprocesar toda la búsqueda, incluso publicaciones fuera de la página visible.
export const buscarPublicacionesSinImagen = async (fetchPage, params) => {
    const { sinImagen, pagina, Pagina, recordsPorPagina, RecordsPorPagina, ...filtros } = params;
    const publicaciones = new Map();
    let paginas = 1;
    for (let page = 1; page <= paginas; page += 1) {
        const response = await fetchPage({ ...filtros, pagina: page, recordsPorPagina: 50 });
        const elementos = obtenerElementosPublicaciones(response);
        if (page === 1) {
            const total = Number(response?.total ?? response?.Total ?? response?.cantidadTotal ?? 0);
            paginas = Number(response?.totalPages ?? response?.TotalPages ?? response?.cantidadPagina)
                || Math.ceil(total / (Number(response?.pageSize ?? response?.PageSize) || elementos.length || 50)) || 1;
        }
        if (!elementos.length && page < paginas) {
            throw new Error('La búsqueda cambió mientras se consultaban las publicaciones. Volvé a buscar.');
        }
        elementos.filter(esPublicacionSinImagen).forEach(publicacion => {
            const id = Number(publicacion.idPublicacion ?? publicacion.IdPublicacion);
            if (!Number.isInteger(id) || id <= 0) throw new Error('Publicación sin ID válido.');
            publicaciones.set(id, { ...publicacion, idPublicacion: id });
        });
    }
    return [...publicaciones.values()];
};

const estadoPublicacion = (publicacion) => String(publicacion.estado ?? publicacion.Estado ?? '').toLowerCase();

export const paginarPublicacionesSinImagen = (elementos, pagina = 1) => ({
    content: elementos.slice((pagina - 1) * 10, pagina * 10),
    total: elementos.length,
    totalPages: Math.ceil(elementos.length / 10),
    page: pagina,
    activos: elementos.filter(p => ['activo', '1'].includes(estadoPublicacion(p))).length,
    enRevision: elementos.filter(p => ['en_revision', '4'].includes(estadoPublicacion(p))).length,
    conStockNoPublicados: elementos.filter(p => Number(p.stock ?? p.Stock) > 0
        && !['activo', '1'].includes(estadoPublicacion(p))).length
});
